export { APIService as default };
import axios from 'axios';
import auth_jwt_useJwt from '@/auth/jwt/useJwt';
import libs_i18n_index from '@/libs/i18n/index';
import router from '@/router';
import store from '@/store';

let isRefreshing = false;
let refreshSubscribers = [];

function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

function onRefreshed(token) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

class APIService {
  async api(
    { url, method },
    data,
    options = {
      show_loader: true,
      http_headers: {},
      api_version: undefined,
      isLoading: false,
    }
  ) {
    let flag = false;
    try {
      if (options.isLoading) {
        const current = store.state.user.isLoadingArray || [];
        store.commit('user/setLoadingArray', [...current, 1]);
        flag = true;
      }

      const headers = {
        'Accept-Language': (libs_i18n_index && libs_i18n_index.vm && libs_i18n_index.vm.locale) || 'en',
      };
      if (typeof FormData === 'undefined' || !(data instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }
      const token = localStorage.getItem(auth_jwt_useJwt.jwtConfig.storageTokenKeyName);
      if (token) {
        headers.authorization = `Bearer ${token}`;
      }

      let finalUrl = url;
      if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
        if (!finalUrl.startsWith('/')) {
          finalUrl = '/' + finalUrl;
        }
        const topPrefixes = [
          '/health',
          '/auth',
          '/jwt',
          '/api',
          '/p2p',
          '/asset',
          '/assets',
          '/trx',
          '/usdt',
          '/kyc',
          '/sys',
        ];
        const matchesTop = topPrefixes.some(
          (prefix) => finalUrl === prefix || finalUrl.startsWith(prefix + '/')
        );
        if (!matchesTop) {
          finalUrl = '/api/v1' + finalUrl;
        }
      }

      const config = {
        method,
        url: finalUrl,
        headers: {
          ...headers,
          ...options.http_headers,
        },
      };
      if (method === 'GET' && data) {
        config.params = data;
      } else {
        config.data = data;
      }

      const res = await axios(config);

      if (options.isLoading) {
        const current = store.state.user.isLoadingArray || [];
        store.commit('user/setLoadingArray', current.slice(0, -1));
        flag = false;
      }

      // Check if response contains business-level session expiration code 553
      if (
        res &&
        res.data &&
        res.data.result &&
        res.data.result.errors &&
        res.data.result.errors.length &&
        res.data.result.errors[0] &&
        String(res.data.result.errors[0].code) === '553'
      ) {
        return this.handleTokenRefresh(config, options);
      }

      if (
        res &&
        res.data &&
        res.data.result &&
        res.data.result.errors &&
        res.data.result.errors.length &&
        res.data.result.errors[0].message
      ) {
        return {
          error: {
            ...res.data.result.errors[0],
          },
        };
      }

      if (res && res.data) {
        return res.data;
      }
      return res;
    } catch (err) {
      if (flag && options.isLoading) {
        const current = store.state.user.isLoadingArray || [];
        store.commit('user/setLoadingArray', current.slice(0, -1));
        flag = false;
      }

      const status = err.response ? err.response.status : null;
      const originalConfig = err.config;

      // Automatically refresh session on 401 Unauthorized
      if (status === 401 && originalConfig && !originalConfig._retry) {
        return this.handleTokenRefresh(originalConfig, options);
      }

      if (
        err.response &&
        err.response.data &&
        err.response.data.result &&
        err.response.data.result.errors &&
        err.response.data.result.errors.length &&
        err.response.data.result.errors[0].message
      ) {
        return {
          error: {
            ...err.response.data.result.errors[0],
          },
        };
      }

      if (err.response && err.response.data) {
        return err.response.data;
      }

      console.error('API Error:', err);
      return {
        error: {
          header: { status: 'ERROR' },
          message: err.message || 'Something went wrong',
        },
      };
    }
  }

  async handleTokenRefresh(originalConfig, options) {
    const refreshToken = localStorage.getItem(auth_jwt_useJwt.jwtConfig.storageRefreshTokenKeyName);

    if (!refreshToken) {
      this.logout();
      return { error: { message: 'Session expired. Please log in again.' } };
    }

    if (!isRefreshing) {
      isRefreshing = true;
      originalConfig._retry = true;

      try {
        const refreshResponse = await axios.post('/auth/refreshToken', {
          refreshToken,
        });

        const newAccessToken =
          refreshResponse.data.accessToken ||
          (refreshResponse.data.result && refreshResponse.data.result.accessToken);
        const newRefreshToken =
          refreshResponse.data.refreshToken ||
          (refreshResponse.data.result && refreshResponse.data.result.refreshToken);
        const userInfo =
          refreshResponse.data.userInfo ||
          (refreshResponse.data.result && refreshResponse.data.result.userInfo);

        if (newAccessToken) {
          localStorage.setItem(auth_jwt_useJwt.jwtConfig.storageTokenKeyName, newAccessToken);
          if (newRefreshToken) {
            localStorage.setItem(
              auth_jwt_useJwt.jwtConfig.storageRefreshTokenKeyName,
              newRefreshToken
            );
          }
          if (userInfo) {
            localStorage.setItem('userInfo', JSON.stringify(userInfo));
          }

          isRefreshing = false;
          onRefreshed(newAccessToken);

          // Retry the original failed request with the new access token
          originalConfig.headers = originalConfig.headers || {};
          originalConfig.headers.authorization = `Bearer ${newAccessToken}`;
          const retryRes = await axios(originalConfig);
          return retryRes.data;
        } else {
          isRefreshing = false;
          this.logout();
          return { error: { message: 'Failed to refresh session.' } };
        }
      } catch (refreshErr) {
        isRefreshing = false;
        refreshSubscribers = [];
        this.logout();
        return { error: { message: 'Session refresh failed. Please log in again.' } };
      }
    } else {
      // Multiple requests in-flight: wait for ongoing refresh to finish
      return new Promise((resolve) => {
        subscribeTokenRefresh(async (newToken) => {
          if (newToken) {
            originalConfig._retry = true;
            originalConfig.headers = originalConfig.headers || {};
            originalConfig.headers.authorization = `Bearer ${newToken}`;
            try {
              const retryRes = await axios(originalConfig);
              resolve(retryRes.data);
            } catch (retryErr) {
              resolve(retryErr.response ? retryErr.response.data : { error: { message: retryErr.message } });
            }
          } else {
            resolve({ error: { message: 'Session expired' } });
          }
        });
      });
    }
  }

  logout() {
    localStorage.removeItem(auth_jwt_useJwt.jwtConfig.storageTokenKeyName);
    localStorage.removeItem(auth_jwt_useJwt.jwtConfig.storageRefreshTokenKeyName);
    localStorage.removeItem('userInfo');
    if (router.currentRoute && router.currentRoute.name !== 'auth-login') {
      router.push({ name: 'auth-login' }).catch(() => {});
    }
    return true;
  }
}
